/**
 * Vehicle Finder Mobile - Widget interactif 5 étapes
 * Version: 1.2 - Avec recherche universelle + scroll + pills cliquables
 */

class VehicleFinderMobile {
  constructor() {
    this.state = {
      currentStep: 1,
      selection: {
        brand: null,
        model: null,
        type: null,
        year: null,
        technology: null
      },
      data: null,
      useRealData: false,
      debug: true,
      showAllBrands: false,
      initialBrandsToShow: 8,
      searchTerm: ''
    };

    this.productCache = new Map();
    this.init();
  }

  // ==================== INIT ====================

  async init() {
    const cfg = window.vfmConfig || {};
    this.state.useRealData = !!cfg.useRealData;
    this.state.debug = cfg.debug !== undefined ? !!cfg.debug : true;
    this.state.initialBrandsToShow = cfg.initialBrandsToShow || 8;

    this.log('🚀 Init VehicleFinderMobile', {
      useRealData: this.state.useRealData,
      initialBrandsToShow: this.state.initialBrandsToShow
    });

    await this.loadData();
    if (!this.state.data) this.loadDemoData();

    this.renderStep();
    this.setupEventListeners();
    this.setupPillsNavigation();
  }

  async loadData() {
    if (this.state.useRealData) {
      const ok = await this.loadRealDataFromAssets();
      if (!ok) this.loadDemoData();
      return;
    }
    this.loadDemoData();
  }

  // ==================== REAL DATA FROM ASSETS ====================

  async loadRealDataFromAssets() {
    const cfg = window.vfmConfig || {};
    const brandsUrl = cfg.brandsUrl;
    const assetsBase = cfg.assetsBase;

    if (!brandsUrl || !assetsBase) {
      this.log('⚠️ Missing brandsUrl/assetsBase in vfmConfig');
      return false;
    }

    try {
      const brandsJson = await fetch(brandsUrl, { credentials: 'same-origin' }).then(r => r.json());
      const brands = Array.isArray(brandsJson?.brands) ? brandsJson.brands : [];

      if (!brands.length) {
        this.log('⚠️ brands.json empty');
        return false;
      }

      const acc = {
        brands: [],
        models: {},
        types: {},
        years: {},
        technologies: {},
        packs: {}
      };

      const setAdd = (obj, key, val) => {
        if (!obj[key]) obj[key] = new Set();
        obj[key].add(val);
      };

      const ensureArr = (setOrArr) =>
        Array.from(setOrArr || []).sort((a, b) => String(a).localeCompare(String(b)));

      for (const brand of brands) {
        const safe = String(brand || '').toUpperCase().replace(/[^\w\-]+/g, '_');
        const brandUrl = assetsBase + `vf_brand_${safe}.json`;

        let brandData = null;
        try {
          const r = await fetch(brandUrl, { credentials: 'same-origin' });
          if (!r.ok) {
            this.log(`⚠️ Brand JSON fetch failed: ${brandUrl} (${r.status})`);
            continue;
          }
          brandData = await r.json();
        } catch (e) {
          this.log(`⚠️ Brand JSON parse failed: ${brandUrl}`, e);
          continue;
        }

        const modelsObj = brandData?.models || {};
        const modelNames = Object.keys(modelsObj);

        acc.brands.push(brand);
        acc.models[brand] = modelNames.sort((a, b) => a.localeCompare(b));

        for (const model of modelNames) {
          const typesObj = modelsObj[model] || {};
          const typeNames = Object.keys(typesObj);

          const modelKey = `${brand}|${model}`;
          typeNames.forEach(ty => setAdd(acc.types, modelKey, ty));

          for (const type of typeNames) {
            const yearsObj = typesObj[type] || {};
            const yearNames = Object.keys(yearsObj);

            const typeKey = `${brand}|${model}|${type}`;
            yearNames.forEach(y => setAdd(acc.years, typeKey, y));

            for (const year of yearNames) {
              const techObj = yearsObj[year] || {};
              const techNames = Object.keys(techObj);

              const yearKey = `${brand}|${model}|${type}|${year}`;
              techNames.forEach(te => setAdd(acc.technologies, yearKey, te));

              for (const tech of techNames) {
                const packsArr = Array.isArray(techObj[tech]) ? techObj[tech] : [];
                const packsKey = `${brand}|${model}|${type}|${year}|${tech}`;
                if (!acc.packs[packsKey]) acc.packs[packsKey] = [];
                packsArr.forEach(p => acc.packs[packsKey].push(p));
              }
            }
          }
        }
      }

      acc.brands = acc.brands.sort((a, b) => a.localeCompare(b));
      acc.types = Object.fromEntries(Object.entries(acc.types).map(([k, v]) => [k, ensureArr(v)]));
      acc.years = Object.fromEntries(Object.entries(acc.years).map(([k, v]) => [k, ensureArr(v)]));
      acc.technologies = Object.fromEntries(Object.entries(acc.technologies).map(([k, v]) => [k, ensureArr(v)]));

      this.state.data = acc;
      this.log('✅ Real data loaded', { brands: acc.brands.length });
      return acc.brands.length > 0;

    } catch (e) {
      console.error('[VFM] loadRealDataFromAssets error', e);
      return false;
    }
  }

  // ==================== DEMO DATA ====================

  loadDemoData() {
    this.state.data = {
      brands: [
        'AUDI', 'BMW', 'MERCEDES', 'VOLKSWAGEN', 'RENAULT',
        'PEUGEOT', 'CITROËN', 'FORD', 'OPEL', 'NISSAN',
        'TOYOTA', 'HONDA', 'HYUNDAI', 'KIA', 'SEAT',
        'SKODA', 'MAZDA', 'FIAT', 'ALFA ROMEO', 'JEEP'
      ],
      models: {
        'AUDI': ['A1', 'A3', 'A4', 'A5', 'A6', 'Q3', 'Q5', 'Q7'],
        'BMW': ['Serie 1', 'Serie 3', 'Serie 5', 'X1', 'X3', 'X5'],
        'MERCEDES': ['Classe A', 'Classe C', 'Classe E', 'GLA', 'GLC'],
        'VOLKSWAGEN': ['Golf', 'Polo', 'Passat', 'Tiguan', 'T-Roc'],
        'RENAULT': ['Clio', 'Megane', 'Captur', 'Kadjar', 'Scenic'],
        'PEUGEOT': ['208', '308', '2008', '3008', '5008'],
        'CITROËN': ['C3', 'C4', 'C5', 'Berlingo'],
        'FORD': ['Fiesta', 'Focus', 'Kuga', 'Puma'],
        'OPEL': ['Corsa', 'Astra', 'Crossland', 'Mokka'],
        'NISSAN': ['Micra', 'Juke', 'Qashqai', 'X-Trail']
      },
      types: {},
      years: {},
      technologies: {},
      packs: {}
    };

    Object.keys(this.state.data.models).forEach(brand => {
      this.state.data.models[brand].forEach(model => {
        const key = `${brand}|${model}`;
        this.state.data.types[key] = this.generateTypes();
      });
    });

    this.log('📦 Demo data loaded');
  }

  generateTypes() {
    const engines = ['1.0 TSI', '1.2 TSI', '1.4 TSI', '1.5 TSI', '2.0 TSI', '1.6 TDI', '2.0 TDI'];
    return engines.slice(0, Math.floor(Math.random() * 4) + 3);
  }

  generateYears() {
    return ['2010 à 2013', '2013 à 2016', '2016 à 2020', '2020 à 2023'];
  }

  generateTechnologies() {
    return ['Standard', 'Avec Xénon', 'Avec LED'];
  }

  generateDemoPacks() {
    return [
      {
        pack_type: 'Complet',
        price: 189.9,
        compare_price: 209.9,
        functions: 'Feux de croisement, Feux de route, Antibrouillard'
      },
      {
        pack_type: 'Extérieur',
        price: 129.9,
        compare_price: 149.9,
        functions: 'Feux de croisement, Feux de route'
      }
    ];
  }

  // ==================== PILLS NAVIGATION ====================

  setupPillsNavigation() {
    document.querySelectorAll('.vfd-pill[data-step]').forEach(pill => {
      pill.addEventListener('click', () => {
        const targetStep = parseInt(pill.dataset.step);
        
        // Peut revenir en arrière uniquement
        if (targetStep < this.state.currentStep) {
          this.log('🔙 Retour à l\'étape', targetStep);
          
          // Si on est sur l'écran des résultats (step 6), il faut réafficher le contenu
          const content = document.getElementById('vfmContent');
          const results = document.getElementById('vfmResults');
          
          if (this.state.currentStep === 6) {
            if (content) content.classList.remove('hidden');
            if (results) results.classList.add('hidden');
          }
          
          // Réinitialiser les sélections après cette étape
          const stepKeys = ['brand', 'model', 'type', 'year', 'technology'];
          for (let i = targetStep; i < stepKeys.length; i++) {
            this.state.selection[stepKeys[i]] = null;
          }
          
          this.state.currentStep = targetStep;
          this.state.showAllBrands = false;
          this.state.searchTerm = '';
          
          // Scroll vers le haut
          this.scrollToTop();
          
          this.renderStep();
        }
      });
    });
  }

  // ==================== UI RENDERING ====================

  renderStep() {
    this.updateProgressPills();
    this.updateStepTitle();
    this.updateSearchBox();
    this.updateNavigation();
    this.renderOptions();
  }

  updateProgressPills() {
    for (let i = 1; i <= 5; i++) {
      const pill = document.querySelector(`[data-step="${i}"]`);
      if (!pill) continue;

      pill.classList.remove('active', 'completed', 'disabled');

      if (i < this.state.currentStep) {
        pill.classList.add('completed');
      } else if (i === this.state.currentStep) {
        pill.classList.add('active');
      } else {
        pill.classList.add('disabled');
      }
    }
  }

  updateStepTitle() {
    const titles = {
      1: 'Choisissez votre marque',
      2: 'Sélectionnez le modèle',
      3: 'Type de motorisation',
      4: 'Année de production',
      5: 'Type de phares'
    };

    const titleEl = document.getElementById('vfmStepTitle');
    if (titleEl) {
      titleEl.textContent = titles[this.state.currentStep] || 'Étape';
    }
  }

  updateSearchBox() {
    const searchBox = document.getElementById('vfmSearchBox');
    const searchInput = document.getElementById('vfmSearchInput');

    const showSearch = this.state.currentStep <= 2;

    if (searchBox) {
      searchBox.classList.toggle('hidden', !showSearch);
    }

    if (searchInput && showSearch) {
      searchInput.value = this.state.searchTerm || '';
      searchInput.placeholder = this.state.currentStep === 1
        ? '🔍 Rechercher une marque...'
        : '🔍 Rechercher un modèle...';
    }
  }

  updateNavigation() {
    const backBtn = document.getElementById('vfmBtnBack');
    const nextBtn = document.getElementById('vfmBtnNext');

    if (backBtn) {
      // À l'étape 5, transformer le bouton "Retour" en "Nouvelle recherche"
      if (this.state.currentStep === 5) {
        backBtn.textContent = '← Nouvelle recherche';
        backBtn.classList.remove('hidden');
      } else {
        backBtn.textContent = '← Retour';
        backBtn.classList.toggle('hidden', this.state.currentStep === 1);
      }
    }

    if (nextBtn) {
      nextBtn.disabled = true;
    }
  }

  renderOptions() {
    const container = document.getElementById('vfmOptionsContainer');
    const showMoreBtn = document.getElementById('vfmShowMore');

    if (!container) return;

    let options = [];
    let useBrandGrid = false;

    switch (this.state.currentStep) {
      case 1:
        options = this.state.data.brands || [];
        useBrandGrid = true;
        break;

      case 2:
        options = this.state.data.models?.[this.state.selection.brand] || [];
        break;

      case 3: {
        const modelKey = `${this.state.selection.brand}|${this.state.selection.model}`;
        options = this.state.data.types?.[modelKey] || this.generateTypes();
        break;
      }

      case 4: {
        const typeKey = `${this.state.selection.brand}|${this.state.selection.model}|${this.state.selection.type}`;
        options = this.state.data.years?.[typeKey] || this.generateYears();
        break;
      }

      case 5: {
        const yearKey = `${this.state.selection.brand}|${this.state.selection.model}|${this.state.selection.type}|${this.state.selection.year}`;
        options = this.state.data.technologies?.[yearKey] || this.generateTechnologies();
        break;
      }

      case 6:
        this.showResults();
        return;
    }

    if (!Array.isArray(options)) options = [];

    // 🚀 AUTO-SÉLECTION SI UNE SEULE OPTION
    if (options.length === 1 && this.state.currentStep > 1) {
      this.log('⚡ Auto-sélection:', options[0]);
      setTimeout(() => {
        this.selectOption(options[0], { target: { closest: () => null } });
      }, 300);
      
      container.innerHTML = `
        <div style="text-align: center; padding: 20px;">
          <p style="color: #666; font-size: 14px;">✓ ${this.escapeHtml(options[0])} sélectionné automatiquement</p>
        </div>
      `;
      return;
    }

    if (useBrandGrid) {
      // ÉTAPE 1 : Marques avec recherche universelle
      this.renderBrandsWithSearch(options, container, showMoreBtn);
    } else {
      // ÉTAPES 2-5 : Liste normale
      container.innerHTML = `
        <div class="vfm-options-list">
          ${options.map(opt => `
            <div class="vfm-option-item" data-value="${this.escapeHtml(opt)}">
              <span class="vfm-option-text">${this.escapeHtml(opt)}</span>
              <span class="vfm-option-arrow">→</span>
            </div>
          `).join('')}
        </div>
      `;

      if (showMoreBtn) {
        showMoreBtn.classList.add('hidden');
      }
    }

    container.querySelectorAll('.vfm-brand-pill, .vfm-option-item').forEach(el => {
      el.addEventListener('click', e => this.selectOption(el.dataset.value, e));
    });
  }

  renderBrandsWithSearch(allBrands, container, showMoreBtn) {
    const searchTerm = this.state.searchTerm.toLowerCase().trim();
    
    // Filtrer les marques selon la recherche
    let filteredBrands = allBrands;
    if (searchTerm) {
      filteredBrands = allBrands.filter(brand => 
        brand.toLowerCase().includes(searchTerm)
      );
    }

    // Déterminer les marques à afficher
    let brandsToDisplay = filteredBrands;
    let showButton = false;

    if (!searchTerm && !this.state.showAllBrands) {
      // Mode par défaut : afficher seulement les X premières
      brandsToDisplay = filteredBrands.slice(0, this.state.initialBrandsToShow);
      showButton = allBrands.length > this.state.initialBrandsToShow;
    }

    this.log('🔍 Recherche:', {
      searchTerm,
      total: allBrands.length,
      filtered: filteredBrands.length,
      displayed: brandsToDisplay.length
    });

    // Afficher les marques
    if (brandsToDisplay.length === 0) {
      container.innerHTML = `
        <div style="text-align: center; padding: 20px; color: #999;">
          <p>Aucune marque trouvée pour "${this.escapeHtml(searchTerm)}"</p>
        </div>
      `;
    } else {
      container.innerHTML = `
        <div class="vfm-brands-pills">
          ${brandsToDisplay.map(brand => `
            <div class="vfm-brand-pill" data-value="${this.escapeHtml(brand)}">
              ${this.escapeHtml(brand)}
            </div>
          `).join('')}
        </div>
      `;
    }

    // Gérer le bouton "Afficher toutes les marques"
    if (showMoreBtn) {
      if (showButton) {
        showMoreBtn.style.display = 'block'; // Afficher le bouton
        showMoreBtn.classList.remove('hidden');
        showMoreBtn.textContent = this.state.showAllBrands
          ? 'Voir moins de marques'
          : `Afficher toutes les marques (${allBrands.length})`;
        showMoreBtn.classList.toggle('expanded', this.state.showAllBrands);
      } else {
        showMoreBtn.style.display = 'none'; // Cacher le bouton
        showMoreBtn.classList.add('hidden');
      }
    }
  }

  selectOption(value, event) {
    const stepKeys = ['brand', 'model', 'type', 'year', 'technology'];
    const key = stepKeys[this.state.currentStep - 1];
    this.state.selection[key] = value;

    document.querySelectorAll('.vfm-brand-pill, .vfm-option-item').forEach(el => {
      el.classList.remove('selected');
    });

    const clicked = event.target.closest('.vfm-brand-pill, .vfm-option-item');
    if (clicked) clicked.classList.add('selected');

    this.log(`✅ Selected ${key}:`, value);

    // Réinitialiser la recherche
    this.state.searchTerm = '';

    // Scroll vers le haut
    this.scrollToTop();

    setTimeout(() => {
      if (this.state.currentStep < 6) {
        this.state.currentStep++;
        this.state.showAllBrands = false;
        this.renderStep();
      }
    }, 300);
  }

  // ==================== SCROLL ====================

  scrollToTop() {
    const wrapper = document.getElementById('vfmWrapper');
    if (wrapper) {
      wrapper.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    }
  }

  // ==================== RESULTS ====================

  showResults() {
    const content = document.getElementById('vfmContent');
    const results = document.getElementById('vfmResults');

    if (content) content.classList.add('hidden');
    if (results) results.classList.remove('hidden');

    const vehicleInfo = `${this.state.selection.brand} ${this.state.selection.model} ${this.state.selection.type} (${this.state.selection.year}) - ${this.state.selection.technology}`;

    const vehicleEl = document.getElementById('vfmResultsVehicle');
    if (vehicleEl) vehicleEl.textContent = vehicleInfo;

    const key = `${this.state.selection.brand}|${this.state.selection.model}|${this.state.selection.type}|${this.state.selection.year}|${this.state.selection.technology}`;

    const products = this.state.data.packs?.[key] || this.generateDemoPacks();

    this.log('📦 Results', { key, productsCount: products.length });

    this.renderProducts(products);
  }

  renderProducts(products) {
    const container = document.getElementById('vfmResultsList');
    if (!container) return;

    container.innerHTML = (products || []).map((product, index) => {
      const handle = product.handle || '';
      const price = this.toMoney(product.price);
      const comparePrice = this.toMoney(product.compare_price);

      const current = price ?? comparePrice;
      const original = price != null && comparePrice != null && comparePrice > price ? comparePrice : null;

      const savings = original != null && current != null
        ? (((original - current) / original) * 100).toFixed(0)
        : null;

      const priceHtml = current != null
        ? `${Number(current).toFixed(2)}€`
        : `<span class="vfm-price-loading" data-price-handle="${this.escapeHtml(handle)}">Voir le prix</span>`;

      const originalHtml = original != null
        ? `<span class="vfm-price-original" data-compare-handle="${this.escapeHtml(handle)}">${original.toFixed(2)}€</span>`
        : '';

      const saveHtml = savings != null
        ? `<span class="vfm-price-save" data-save-handle="${this.escapeHtml(handle)}">-${savings}%</span>`
        : '';

      // 🏆 BADGE BESTSELLER (premier produit = le plus populaire)
      const bestsellerBadge = index === 0 
        ? `<span class="vfm-product-badge vfm-badge-bestseller">⭐ BESTSELLER</span>`
        : '';

      // 📦 INDICATEUR DE STOCK INTELLIGENT (basé sur position)
      const stockInfo = this.generateSmartStock(index, products.length);

      return `
        <div class="vfm-product-card">
          <div class="vfm-product-header">
            <div class="vfm-product-image" data-img-handle="${this.escapeHtml(handle)}">
              💡
            </div>
            <div class="vfm-product-info">
              ${bestsellerBadge}
              <span class="vfm-product-badge">${this.escapeHtml(product.pack_type || 'Pack')}</span>
              <h4 class="vfm-product-title">
                ${this.escapeHtml(product.title || `Pack LED ${this.state.selection.brand}`)}
              </h4>
              <div class="vfm-product-price">
                <span class="vfm-price-current">${priceHtml}</span>
                ${originalHtml}
                ${saveHtml}
              </div>
              ${stockInfo}
            </div>
          </div>
          <div class="vfm-product-actions">
            <button class="vfm-btn-cart" data-handle="${this.escapeHtml(handle)}">
              🛒 Ajouter
            </button>
            <button class="vfm-btn-details" data-handle="${this.escapeHtml(handle)}">
              Détails
            </button>
          </div>
        </div>
      `;
    }).join('');

    container.querySelectorAll('.vfm-btn-cart').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn.dataset.handle));
    });

    container.querySelectorAll('.vfm-btn-details').forEach(btn => {
      btn.addEventListener('click', () => this.viewDetails(btn.dataset.handle));
    });

    setTimeout(() => this.hydrateResults(products), 0);
  }

  // 📦 GÉNÉRATEUR DE STOCK INTELLIGENT
  generateSmartStock(index, total) {
    // Produit 1 (bestseller) : Stock élevé mais avec urgence
    if (index === 0) {
      const stock = Math.floor(Math.random() * 5) + 8; // 8-12 en stock
      return `<p class="vfm-stock-high">✅ ${stock} en stock - Expédition immédiate</p>`;
    }
    
    // Produit 2 : Stock moyen avec légère urgence
    if (index === 1) {
      const stock = Math.floor(Math.random() * 3) + 4; // 4-6 en stock
      return `<p class="vfm-stock-medium">⚡ Plus que ${stock} en stock</p>`;
    }
    
    // Produit 3+ : Stock limité pour créer urgence
    if (index >= 2) {
      const stock = Math.floor(Math.random() * 2) + 2; // 2-3 en stock
      return `<p class="vfm-stock-low">🔥 Dernières pièces - Plus que ${stock} disponibles</p>`;
    }
    
    return '';
  }

  async hydrateResults(products) {
    const handles = Array.from(new Set((products || []).map(p => p.handle).filter(Boolean)));

    for (const handle of handles) {
      const info = await this.fetchProductInfo(handle);
      if (!info) continue;

      if (info.imageUrl) {
        const imgEls = document.querySelectorAll(`[data-img-handle="${this.cssEscape(handle)}"]`);
        imgEls.forEach(el => {
          el.style.backgroundImage = `url(${info.imageUrl})`;
          el.style.backgroundSize = 'contain';
          el.style.backgroundPosition = 'center';
          el.style.backgroundRepeat = 'no-repeat';
          el.textContent = '';
        });
      }

      if (info.price != null) {
        const priceEls = document.querySelectorAll(`[data-price-handle="${this.cssEscape(handle)}"]`);
        priceEls.forEach(el => {
          el.textContent = `${Number(info.price).toFixed(2)}€`;
          el.classList.remove('vfm-price-loading');
        });
      }

      if (info.comparePrice != null) {
        const compareEls = document.querySelectorAll(`[data-compare-handle="${this.cssEscape(handle)}"]`);
        compareEls.forEach(el => {
          el.textContent = `${Number(info.comparePrice).toFixed(2)}€`;
        });
      }

      if (info.price != null && info.comparePrice != null && info.comparePrice > info.price) {
        const saveEls = document.querySelectorAll(`[data-save-handle="${this.cssEscape(handle)}"]`);
        const savings = (((info.comparePrice - info.price) / info.comparePrice) * 100).toFixed(0);
        saveEls.forEach(el => {
          el.textContent = `-${savings}%`;
        });
      }
    }
  }

  async fetchProductInfo(handle) {
    if (!handle) return null;
    if (this.productCache.has(handle)) return this.productCache.get(handle);

    try {
      const p = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' }).then(r => r.json());

      const imageUrl = p?.featured_image || (Array.isArray(p?.images) ? p.images[0] : null) || null;

      const v = p?.variants?.[0] || null;
      const centsPrice = this.toCents(v?.price);
      const centsCompare = this.toCents(v?.compare_at_price);

      const price = centsPrice != null ? centsPrice / 100 : null;
      const comparePrice = centsCompare != null ? centsCompare / 100 : null;

      const info = { imageUrl, price, comparePrice };
      this.productCache.set(handle, info);
      return info;
    } catch {
      this.productCache.set(handle, null);
      return null;
    }
  }

  // ==================== EVENT LISTENERS ====================

  setupEventListeners() {
    const backBtn = document.getElementById('vfmBtnBack');
    const nextBtn = document.getElementById('vfmBtnNext');
    const resetBtn = document.getElementById('vfmBtnReset');
    const showMoreBtn = document.getElementById('vfmShowMore');
    const searchInput = document.getElementById('vfmSearchInput');

    if (backBtn) {
      backBtn.addEventListener('click', () => this.goBack());
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.goNext());
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.reset());
    }

    if (showMoreBtn) {
      showMoreBtn.addEventListener('click', () => this.toggleShowAllBrands());
    }

    if (searchInput) {
      searchInput.addEventListener('input', e => this.handleSearch(e.target.value));
    }
  }

  goBack() {
    // À l'étape 5, le bouton "Nouvelle recherche" appelle reset()
    if (this.state.currentStep === 5) {
      this.reset();
      return;
    }
    
    if (this.state.currentStep > 1) {
      this.state.currentStep--;
      const stepKeys = ['brand', 'model', 'type', 'year', 'technology'];
      this.state.selection[stepKeys[this.state.currentStep - 1]] = null;
      
      this.scrollToTop();
      
      this.renderStep();
    }
  }

  goNext() {
    if (this.state.currentStep < 6) {
      this.state.currentStep++;
      this.renderStep();
    }
  }

  reset() {
    this.state.currentStep = 1;
    this.state.selection = {
      brand: null,
      model: null,
      type: null,
      year: null,
      technology: null
    };
    this.state.showAllBrands = false;
    this.state.searchTerm = '';

    const content = document.getElementById('vfmContent');
    const results = document.getElementById('vfmResults');

    if (content) content.classList.remove('hidden');
    if (results) results.classList.add('hidden');

    this.scrollToTop();

    this.renderStep();
  }

  toggleShowAllBrands() {
    this.state.showAllBrands = !this.state.showAllBrands;
    this.log('🔄 Toggle show all brands:', this.state.showAllBrands);
    
    if (this.state.showAllBrands) {
      setTimeout(() => {
        const optionsContainer = document.getElementById('vfmOptionsContainer');
        if (optionsContainer) {
          optionsContainer.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    }
    
    this.renderOptions();
  }

  handleSearch(term) {
    this.state.searchTerm = term;
    this.log('🔍 Search term:', term);
    
    // Re-render pour appliquer le filtre de recherche
    if (this.state.currentStep === 1) {
      this.renderOptions();
    } else {
      // Pour les autres étapes, filtre classique
      const searchTerm = String(term || '').toLowerCase();
      document.querySelectorAll('.vfm-option-item').forEach(el => {
        const text = String(el.textContent || '').toLowerCase();
        el.style.display = text.includes(searchTerm) ? '' : 'none';
      });
    }
  }

  async addToCart(handle) {
    if (!handle) {
      alert('⚠️ Handle manquant.');
      return;
    }

    try {
      const product = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' }).then(r => r.json());
      const variantId = product.variants?.[0]?.id;
      if (!variantId) throw new Error('Variant introuvable');

      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      if (!r.ok) throw new Error(`Cart add failed (${r.status})`);

      window.location.href = '/cart';
    } catch (error) {
      console.error('Erreur addToCart:', error);
      alert('❌ Erreur lors de l\'ajout au panier');
    }
  }

  viewDetails(handle) {
    if (!handle) {
      alert('⚠️ Handle manquant.');
      return;
    }
    window.location.href = `/products/${handle}`;
  }

  // ==================== HELPERS ====================

  toMoney(v) {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'object') {
      if (v.amount != null) return this.toMoney(v.amount);
      if (v.value != null) return this.toMoney(v.value);
      return null;
    }
    const s = String(v).trim();
    if (!s) return null;
    const cleaned = s.replace(/[^\d.,-]/g, '').replace(',', '.');
    const n = Number.parseFloat(cleaned);
    return Number.isFinite(n) ? n : null;
  }

  toCents(v) {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  escapeHtml(text) {
    if (text === null || text === undefined) return '';
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return String(text).replace(/[&<>"']/g, m => map[m]);
  }

  cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(s));
    return String(s).replace(/["\\]/g, '\\$&');
  }

  log(...args) {
    if (this.state.debug) {
      console.log('[VFM]', ...args);
    }
  }
}

// Auto-init
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.vfmInstance = new VehicleFinderMobile();
  });
} else {
  window.vfmInstance = new VehicleFinderMobile();
}
